/**
 * Address Formatting Utilities
 * 
 * Provides utilities for formatting US addresses in a consistent,
 * readable format across the application.
 */

export interface StructuredAddress {
  street_address?: string | null;
  city?: string | null;
  state?: string | null;
  zip_code?: string | null;
  country?: string | null;
  // Legacy field for backward compatibility
  address?: string | null;
}

/**
 * Formats a structured address into a single-line US address format
 * 
 * @param address - Structured address object
 * @returns Formatted address string (e.g., "123 Main St, Springfield, IL 62701")
 * 
 * @example
 * formatAddress({
 *   street_address: "123 Main St",
 *   city: "Springfield",
 *   state: "IL",
 *   zip_code: "62701"
 * })
 * // Returns: "123 Main St, Springfield, IL 62701"
 */
export function formatAddress(address: StructuredAddress): string {
  if (!address) {
    return '';
  }

  const parts: string[] = [];

  // If we have structured fields, use them
  if (address.street_address) {
    parts.push(address.street_address.trim());

    // Add city, state zip on same line
    const cityStateZip: string[] = [];
    if (address.city) {
      cityStateZip.push(address.city.trim());
    }
    if (address.state) {
      cityStateZip.push(address.state.trim());
    }
    if (address.zip_code) {
      cityStateZip.push(address.zip_code.trim());
    }

    if (cityStateZip.length > 0) {
      // Join city and state-zip differently
      // "Springfield, IL 62701" format
      const stateZip = [];
      if (address.state) stateZip.push(address.state.trim());
      if (address.zip_code) stateZip.push(address.zip_code.trim());
      
      const result = [address.street_address.trim()];
      if (address.city) {
        result.push(address.city.trim());
      }
      if (stateZip.length > 0) {
        result.push(stateZip.join(' '));
      }
      
      return result.join(', ');
    }

    return parts.join(', ');
  }

  // Fallback to legacy address field if no structured data
  if (address.address) {
    return address.address.trim();
  }

  return '';
}

/**
 * Formats a structured address into multiple lines for display
 * 
 * @param address - Structured address object
 * @returns Array of address lines
 * 
 * @example
 * formatAddressMultiline({
 *   street_address: "123 Main St",
 *   city: "Springfield",
 *   state: "IL",
 *   zip_code: "62701"
 * })
 * // Returns: ["123 Main St", "Springfield, IL 62701"]
 */
export function formatAddressMultiline(address: StructuredAddress): string[] {
  if (!address) {
    return [];
  }

  const lines: string[] = [];

  // If we have structured fields, use them
  if (address.street_address) {
    lines.push(address.street_address.trim());

    // Add city, state zip on second line
    const cityStateZip: string[] = [];
    if (address.city) {
      cityStateZip.push(address.city.trim());
    }
    
    const stateZip = [];
    if (address.state) stateZip.push(address.state.trim());
    if (address.zip_code) stateZip.push(address.zip_code.trim());
    
    if (address.city && stateZip.length > 0) {
      lines.push(`${address.city.trim()}, ${stateZip.join(' ')}`);
    } else if (address.city) {
      lines.push(address.city.trim());
    } else if (stateZip.length > 0) {
      lines.push(stateZip.join(' '));
    }

    return lines;
  }

  // Fallback to legacy address field
  if (address.address) {
    return [address.address.trim()];
  }

  return [];
}

/**
 * Formats just the city, state, zip portion of an address
 * 
 * @param address - Structured address object
 * @returns Formatted city, state, zip (e.g., "Springfield, IL 62701")
 */
export function formatCityStateZip(address: StructuredAddress): string {
  if (!address) {
    return '';
  }

  const parts: string[] = [];
  
  if (address.city) {
    parts.push(address.city.trim());
  }
  
  const stateZip = [];
  if (address.state) stateZip.push(address.state.trim());
  if (address.zip_code) stateZip.push(address.zip_code.trim());
  
  if (stateZip.length > 0) {
    parts.push(stateZip.join(' '));
  }

  return parts.join(', ');
}

/**
 * Gets a short display version of the address (just street and city)
 * 
 * @param address - Structured address object
 * @returns Short address format (e.g., "123 Main St, Springfield")
 */
export function formatAddressShort(address: StructuredAddress): string {
  if (!address) {
    return '';
  }

  const parts: string[] = [];

  if (address.street_address) {
    parts.push(address.street_address.trim());
  }

  if (address.city) {
    parts.push(address.city.trim());
  }

  if (parts.length > 0) {
    return parts.join(', ');
  }

  // Fallback to legacy address
  if (address.address) {
    return address.address.trim();
  }

  return '';
}

/**
 * Checks if an address has structured data (vs legacy single-field)
 */
export function hasStructuredAddress(address: StructuredAddress): boolean {
  return !!(
    address.street_address ||
    address.city ||
    address.state ||
    address.zip_code
  );
}

/**
 * Gets the display address, preferring structured format
 */
export function getDisplayAddress(address: StructuredAddress): string {
  if (hasStructuredAddress(address)) {
    return formatAddress(address);
  }
  
  return address.address?.trim() || '';
}

